// src/routes/AppRouter.jsx
import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import useAuthStore from '@/store/authStore'
import { ROLES } from '@/constants/roles'
import LoadingSpinner from '@/components/common/LoadingSpinner'

// Layouts (loaded eagerly since they wrap everything)
import DashboardLayout from '@/components/layout/DashboardLayout'

// Auth pages (small, loaded eagerly for fast first paint)
import Login          from '@/pages/auth/Login'
import Register       from '@/pages/auth/Register'
import ForgotPassword from '@/pages/auth/ForgotPassword'

// ─── LAZY-LOADED PAGES ──────────────────────────────────────────────────────

// Public pages
const AdmissionForm = lazy(() => import('@/pages/public/AdmissionForm'))

// Marketing pages
const MarketingLayout = lazy(() => import('@/components/marketing/MarketingLayout'))
const HomePage        = lazy(() => import('@/pages/marketing/HomePage'))
const FeaturesPage    = lazy(() => import('@/pages/marketing/FeaturesPage'))
const PricingPage     = lazy(() => import('@/pages/marketing/PricingPage'))
const ContactPage     = lazy(() => import('@/pages/marketing/ContactPage'))
const AboutPage       = lazy(() => import('@/pages/marketing/AboutPage'))
const PrivacyPage     = lazy(() => import('@/pages/marketing/PrivacyPage'))
const TermsPage       = lazy(() => import('@/pages/marketing/TermsPage'))

// Admin pages
const AdminDashboard     = lazy(() => import('@/pages/admin/Dashboard'))
const Students           = lazy(() => import('@/pages/admin/Students'))
const Classes            = lazy(() => import('@/pages/admin/Classes'))
const FeeReports         = lazy(() => import('@/pages/admin/FeeReports'))
const Notifications      = lazy(() => import('@/pages/admin/Notifications'))
const Insights           = lazy(() => import('@/pages/admin/Insights'))
const AdminAttendance    = lazy(() => import('@/pages/admin/Attendance'))
const Notices            = lazy(() => import('@/pages/admin/Notices'))
const FeeDiscounts       = lazy(() => import('@/pages/admin/FeeDiscounts'))
const ActivityLog        = lazy(() => import('@/pages/admin/ActivityLog'))
const AdmissionsPipeline = lazy(() => import('@/pages/admin/AdmissionsPipeline'))
const Library            = lazy(() => import('@/pages/admin/Library'))
const Transport          = lazy(() => import('@/pages/admin/Transport'))
const Timetable          = lazy(() => import('@/pages/admin/Timetable'))
const AdminGradebook     = lazy(() => import('@/pages/admin/Gradebook'))
const ReportCards        = lazy(() => import('@/pages/admin/ReportCards'))
const StudentIdCards     = lazy(() => import('@/pages/admin/StudentIdCards'))
const AiAssistant        = lazy(() => import('@/pages/admin/AiAssistant'))
const SubjectsList       = lazy(() => import('@/pages/admin/content/SubjectsList'))
const ChaptersList       = lazy(() => import('@/pages/admin/content/ChaptersList'))
const ChapterContent     = lazy(() => import('@/pages/admin/content/ChapterContent'))
const PapersList         = lazy(() => import('@/pages/admin/content/PapersList'))

// Content pages (shared)
const ContentBrowser     = lazy(() => import('@/pages/content/ContentBrowser'))
const MyBookmarks        = lazy(() => import('@/pages/content/MyBookmarks'))

// Practice & Test Engine
const QuestionBanks      = lazy(() => import('@/pages/admin/practice/QuestionBanks'))
const AdminQuestions      = lazy(() => import('@/pages/admin/practice/Questions'))
const AdminPracticeTests  = lazy(() => import('@/pages/admin/practice/PracticeTests'))
const TestHistory        = lazy(() => import('@/pages/practice/TestHistory'))
const TakeTest           = lazy(() => import('@/pages/practice/TakeTest'))
const TestResult         = lazy(() => import('@/pages/practice/TestResult'))
const ExamReadiness      = lazy(() => import('@/pages/practice/ExamReadiness'))

// Feature pages
const WebsiteBuilder  = lazy(() => import('@/pages/builder/WebsiteBuilder'))
const AdmissionsList  = lazy(() => import('@/pages/admissions/AdmissionsList'))
const FeeManagement   = lazy(() => import('@/pages/fees/FeeManagement'))

// Teacher pages
const TeacherDashboard   = lazy(() => import('@/pages/teacher/TeacherDashboard'))
const TeacherAttendance  = lazy(() => import('@/pages/teacher/Attendance'))
const TeacherAssignments = lazy(() => import('@/pages/teacher/Assignments'))
const TeacherGradebook   = lazy(() => import('@/pages/teacher/Gradebook'))
const TeacherOnlineExams = lazy(() => import('@/pages/teacher/OnlineExams'))

// Parent & shared pages
const ParentDashboard = lazy(() => import('@/pages/parent/ParentDashboard'))
const Messages        = lazy(() => import('@/pages/messages/Messages'))

// Student pages
const StudentDashboard   = lazy(() => import('@/pages/student/StudentDashboard'))
const StudentAttendance  = lazy(() => import('@/pages/student/StudentAttendance'))
const StudentTimetable   = lazy(() => import('@/pages/student/StudentTimetable'))
const StudentAssignments = lazy(() => import('@/pages/student/StudentAssignments'))
const StudentExamResults = lazy(() => import('@/pages/student/StudentExamResults'))
const StudentReportCard  = lazy(() => import('@/pages/student/StudentReportCard'))
const StudentFees        = lazy(() => import('@/pages/student/StudentFees'))
const StudentNotices     = lazy(() => import('@/pages/student/StudentNotices'))
const StudentProfile     = lazy(() => import('@/pages/student/StudentProfile'))
const StudentClassmates  = lazy(() => import('@/pages/student/StudentClassmates'))

// Analytics
const SchoolAnalytics  = lazy(() => import('@/pages/admin/analytics/SchoolAnalytics'))
const ClassAnalytics   = lazy(() => import('@/pages/admin/analytics/ClassAnalytics'))
const StudentAnalytics = lazy(() => import('@/pages/analytics/StudentAnalytics'))

// Classroom
const ClassStream       = lazy(() => import('@/pages/classroom/ClassStream'))
const ClassMaterials    = lazy(() => import('@/pages/classroom/ClassMaterials'))
const JoinClass         = lazy(() => import('@/pages/classroom/JoinClass'))
const Discussions       = lazy(() => import('@/pages/classroom/Discussions'))
const DiscussionThread  = lazy(() => import('@/pages/classroom/DiscussionThread'))

// Notification Center
const NotificationCenter      = lazy(() => import('@/pages/notifications/NotificationCenter'))
const NotificationPreferences = lazy(() => import('@/pages/notifications/NotificationPreferences'))
const ReminderManager         = lazy(() => import('@/pages/admin/notifications/ReminderManager'))

// AI Tutor
const AiTutor              = lazy(() => import('@/pages/ai/AiTutor'))
const DoubtSolver          = lazy(() => import('@/pages/ai/DoubtSolver'))
const SmartRecommendations = lazy(() => import('@/pages/ai/SmartRecommendations'))
const StudyPlanView        = lazy(() => import('@/pages/ai/StudyPlanView'))

// Engagement
const LiveQuizzes = lazy(() => import('@/pages/engagement/LiveQuizzes'))
const TakeQuiz    = lazy(() => import('@/pages/engagement/TakeQuiz'))
const Leaderboard = lazy(() => import('@/pages/engagement/Leaderboard'))
const QaForum     = lazy(() => import('@/pages/engagement/QaForum'))
const QaDetail    = lazy(() => import('@/pages/engagement/QaDetail'))

// Billing
const Billing = lazy(() => import('@/pages/billing/Billing'))

// ─── GUARDS ──────────────────────────────────────────────────────────────────

const PrivateRoute = ({ children }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated())
  return isAuthenticated ? children : <Navigate to="/login" replace />
}

const RoleRoute = ({ children, roles }) => {
  const user = useAuthStore((s) => s.user)
  if (!user) return <Navigate to="/login" replace />
  if (!roles.includes(user.role)) return <Navigate to="/dashboard" replace />
  return children
}

// ─── ROUTER ──────────────────────────────────────────────────────────────────

export default function AppRouter() {
  const user = useAuthStore((s) => s.user)

  // Default redirect after login based on role
  const defaultRoute = () => {
    switch (user?.role) {
      case ROLES.ADMIN:       return '/admin/dashboard'
      case ROLES.SUPER_ADMIN: return '/admin/dashboard'
      case ROLES.TEACHER:     return '/teacher/dashboard'
      case ROLES.PARENT:      return '/parent/dashboard'
      case ROLES.STUDENT:     return '/student/dashboard'
      default:                return '/'
    }
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>

          {/* ── AUTH ───────────────────────────────────────────── */}
          <Route path="/login"           element={<Login />} />
          <Route path="/register"        element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password"  element={<ForgotPassword />} />
          <Route path="/apply"           element={<AdmissionForm />} />

          {/* ── MARKETING PAGES (public) ───────────────────────── */}
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
          </Route>

          {/* ── DASHBOARD REDIRECT (authenticated users) ───────── */}
          <Route path="/dashboard" element={
            <PrivateRoute>
              <Navigate to={defaultRoute()} replace />
            </PrivateRoute>
          } />

          {/* ── PROTECTED LAYOUT (no path — children use absolute paths) ── */}
          <Route element={
            <PrivateRoute>
              <DashboardLayout />
            </PrivateRoute>
          }>

            {/* ── Admin ───────────────────────────────────────── */}
            <Route path="/admin/dashboard" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><AdminDashboard /></RoleRoute>} />
            <Route path="/admin/classes" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><Classes /></RoleRoute>} />
            <Route path="/admin/students" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><Students /></RoleRoute>} />
            <Route path="/admin/fee-reports" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><FeeReports /></RoleRoute>} />
            <Route path="/admin/notifications" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><Notifications /></RoleRoute>} />
            <Route path="/admin/insights" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><Insights /></RoleRoute>} />
            <Route path="/admin/attendance" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><AdminAttendance /></RoleRoute>} />
            <Route path="/admin/notices" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><Notices /></RoleRoute>} />
            <Route path="/admin/fee-discounts" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><FeeDiscounts /></RoleRoute>} />
            <Route path="/admin/activity-log" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><ActivityLog /></RoleRoute>} />
            <Route path="/admin/timetable" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><Timetable /></RoleRoute>} />
            <Route path="/admin/gradebook" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><AdminGradebook /></RoleRoute>} />
            <Route path="/admin/report-cards" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><ReportCards /></RoleRoute>} />
            <Route path="/admin/id-cards" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><StudentIdCards /></RoleRoute>} />
            <Route path="/admin/library" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><Library /></RoleRoute>} />
            <Route path="/admin/transport" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><Transport /></RoleRoute>} />
            <Route path="/admin/ai-assistant" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><AiAssistant /></RoleRoute>} />

            {/* ── Analytics ─────────────────────────────────────── */}
            <Route path="/admin/analytics" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><SchoolAnalytics /></RoleRoute>} />
            <Route path="/admin/analytics/class/:classId" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.TEACHER]}><ClassAnalytics /></RoleRoute>} />
            <Route path="/analytics/student/:studentId" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.TEACHER, ROLES.PARENT, ROLES.STUDENT]}><StudentAnalytics /></RoleRoute>} />

            {/* ── Content & Curriculum (Admin) ────────────────── */}
            <Route path="/admin/subjects" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><SubjectsList /></RoleRoute>} />
            <Route path="/admin/subjects/:subjectId/chapters" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><ChaptersList /></RoleRoute>} />
            <Route path="/admin/subjects/:subjectId/chapters/:chapterId/content" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><ChapterContent /></RoleRoute>} />
            <Route path="/admin/papers" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><PapersList /></RoleRoute>} />

            {/* ── Practice & Tests (Admin/Teacher) ──────────── */}
            <Route path="/admin/question-banks" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.TEACHER]}><QuestionBanks /></RoleRoute>} />
            <Route path="/admin/question-banks/:bankId/questions" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.TEACHER]}><AdminQuestions /></RoleRoute>} />
            <Route path="/admin/practice-tests" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.TEACHER]}><AdminPracticeTests /></RoleRoute>} />

            {/* ── Admissions ──────────────────────────────────── */}
            <Route path="/admissions" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><AdmissionsList /></RoleRoute>} />
            <Route path="/admissions/pipeline" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><AdmissionsPipeline /></RoleRoute>} />

            {/* ── Website Builder ─────────────────────────────── */}
            <Route path="/builder" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><WebsiteBuilder /></RoleRoute>} />

            {/* ── Fees ────────────────────────────────────────── */}
            <Route path="/fees" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><FeeManagement /></RoleRoute>} />

            {/* ── Teacher Portal ──────────────────────────────── */}
            <Route path="/teacher/dashboard" element={<RoleRoute roles={[ROLES.TEACHER]}><TeacherDashboard /></RoleRoute>} />
            <Route path="/teacher/attendance" element={<RoleRoute roles={[ROLES.TEACHER]}><TeacherAttendance /></RoleRoute>} />
            <Route path="/teacher/assignments" element={<RoleRoute roles={[ROLES.TEACHER]}><TeacherAssignments /></RoleRoute>} />
            <Route path="/teacher/gradebook" element={<RoleRoute roles={[ROLES.TEACHER]}><TeacherGradebook /></RoleRoute>} />
            <Route path="/teacher/online-exams" element={<RoleRoute roles={[ROLES.TEACHER]}><TeacherOnlineExams /></RoleRoute>} />

            {/* ── Shared ──────────────────────────────────────── */}
            <Route path="/content" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.TEACHER, ROLES.PARENT, ROLES.STUDENT]}><ContentBrowser /></RoleRoute>} />
            <Route path="/bookmarks" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.TEACHER, ROLES.PARENT, ROLES.STUDENT]}><MyBookmarks /></RoleRoute>} />
            <Route path="/messages" element={<RoleRoute roles={[ROLES.TEACHER, ROLES.PARENT, ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.STUDENT]}><Messages /></RoleRoute>} />

            {/* ── Practice (Student / All roles) ────────────── */}
            <Route path="/practice" element={<TestHistory />} />
            <Route path="/practice/test/:testId" element={<TakeTest />} />
            <Route path="/practice/result/:attemptId" element={<TestResult />} />
            <Route path="/practice/readiness" element={<ExamReadiness />} />

            {/* ── Parent Portal ───────────────────────────────── */}
            <Route path="/parent/dashboard" element={<RoleRoute roles={[ROLES.PARENT]}><ParentDashboard /></RoleRoute>} />

            {/* ── Student Portal ─────────────────────────────── */}
            <Route path="/student/dashboard"    element={<RoleRoute roles={[ROLES.STUDENT]}><StudentDashboard /></RoleRoute>} />
            <Route path="/student/attendance"   element={<RoleRoute roles={[ROLES.STUDENT]}><StudentAttendance /></RoleRoute>} />
            <Route path="/student/timetable"    element={<RoleRoute roles={[ROLES.STUDENT]}><StudentTimetable /></RoleRoute>} />
            <Route path="/student/assignments"  element={<RoleRoute roles={[ROLES.STUDENT]}><StudentAssignments /></RoleRoute>} />
            <Route path="/student/exam-results" element={<RoleRoute roles={[ROLES.STUDENT]}><StudentExamResults /></RoleRoute>} />
            <Route path="/student/report-card"  element={<RoleRoute roles={[ROLES.STUDENT]}><StudentReportCard /></RoleRoute>} />
            <Route path="/student/fees"         element={<RoleRoute roles={[ROLES.STUDENT]}><StudentFees /></RoleRoute>} />
            <Route path="/student/notices"      element={<RoleRoute roles={[ROLES.STUDENT]}><StudentNotices /></RoleRoute>} />
            <Route path="/student/profile"      element={<RoleRoute roles={[ROLES.STUDENT]}><StudentProfile /></RoleRoute>} />
            <Route path="/student/classmates"   element={<RoleRoute roles={[ROLES.STUDENT]}><StudentClassmates /></RoleRoute>} />

            {/* ── Classroom ─────────────────────────────────── */}
            <Route path="/classroom/join" element={<RoleRoute roles={[ROLES.PARENT, ROLES.STUDENT]}><JoinClass /></RoleRoute>} />
            <Route path="/classroom/:classId" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.TEACHER, ROLES.PARENT, ROLES.STUDENT]}><ClassStream /></RoleRoute>} />
            <Route path="/classroom/:classId/materials" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.TEACHER, ROLES.PARENT, ROLES.STUDENT]}><ClassMaterials /></RoleRoute>} />

            {/* ── Discussions ────────────────────────────────── */}
            <Route path="/discussions" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.TEACHER, ROLES.PARENT, ROLES.STUDENT]}><Discussions /></RoleRoute>} />
            <Route path="/discussions/:threadId" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.TEACHER, ROLES.PARENT, ROLES.STUDENT]}><DiscussionThread /></RoleRoute>} />

            {/* ── Notification Center (all roles) ──────────────── */}
            <Route path="/notifications" element={<NotificationCenter />} />
            <Route path="/notifications/preferences" element={<NotificationPreferences />} />
            <Route path="/admin/reminders" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN]}><ReminderManager /></RoleRoute>} />

            {/* ── AI Tutor (all roles) ──────────────────────── */}
            <Route path="/ai-tutor" element={<AiTutor />} />
            <Route path="/ai/doubts" element={<DoubtSolver />} />
            <Route path="/ai/recommendations" element={<SmartRecommendations />} />
            <Route path="/ai/study-plan" element={<StudyPlanView />} />

            {/* ── Engagement: Live Quizzes, Leaderboard, Q&A ── */}
            <Route path="/quizzes" element={<RoleRoute roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.TEACHER]}><LiveQuizzes /></RoleRoute>} />
            <Route path="/quiz/:quizId" element={<TakeQuiz />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/qa" element={<QaForum />} />
            <Route path="/qa/:questionId" element={<QaDetail />} />

            {/* ── Billing ─────────────────────────────────────── */}
            <Route path="/billing" element={<Billing />} />

          </Route>

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />

        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
